/**
 * PreRunScreenInput — 3-cell segmented operator callsign (v5 mockup).
 *
 * Two input modes, chosen at build time (see `defaultInputMode`):
 *
 *   • "keyboard" (web / desktop) — real text <input>s: type a char, auto-advance,
 *     backspace retreats. Commits live to metaState on every keystroke.
 *
 *   • "cycle" (native iOS / Capacitor) — TAP-TO-CYCLE on plain <div>s, NO text
 *     input and NO keyboard. This is the iPhone crash fix: focusing a real
 *     <input> inside the #game-container `transform: scale()` stage in a
 *     non-scrolling WKWebView (capacitor.config.ts: scrollEnabled:false,
 *     contentInset:never) makes WebKit run its form-assistant "scroll the field
 *     into view" path, fail to reconcile with the transform, and kill the
 *     web-content process — the app relaunches to the boot screen ("tap a cell →
 *     the game restarts"). <div>s can never take text focus or summon a
 *     keyboard, so that code path is gone entirely. A tap cycles the letter:
 *     top half = previous, bottom half = next (arcade high-score style), via the
 *     already unit-tested `cycleInitialChar`.
 *
 * The deploy gate (PreRunScreen.getInitialsValue) reads cells through the
 * exported `readCellChar` so it works for BOTH a div (textContent) and an
 * <input> (.value) — one accessor, no divergence.
 */
import type { MetaState } from "../core/MetaState.ts";
import {
  cycleInitialChar,
  DEFAULT_INITIALS,
  INITIALS_CHARSET,
  INITIALS_FALLBACK,
  INITIALS_LENGTH,
  sanitizeInitials,
} from "../core/initials.ts";

export interface NameInputHooks {
  /** Fires on every (filtered) keystroke / tap with the current combined value. */
  onInput: (name: string) => void;
}

/** How the callsign cells accept input. */
export type InitialsInputMode = "keyboard" | "cycle";

/**
 * The platform default: native (Capacitor/WKWebView) gets the keyboard-free
 * tap-to-cycle cells; everything else gets real text inputs. `__NATIVE_SHELL__`
 * is the build-time literal (vite define) — `false` for web + tests, `true`
 * only for the Capacitor build.
 */
const defaultInputMode: InitialsInputMode = __NATIVE_SHELL__
  ? "cycle"
  : "keyboard";

/**
 * Read the current character of an initials cell. Works for both modes: `.value`
 * for the <input> (keyboard) cells, `textContent` for the <div> (cycle) cells.
 * Exported so PreRunScreen's deploy gate reads cells the exact same way.
 */
export function readCellChar(el: Element): string {
  if (el instanceof HTMLInputElement) return el.value;
  return el.textContent ?? "";
}

/** Write a character into a cell (mode-agnostic). */
function writeCellChar(el: HTMLElement, ch: string): void {
  if (el instanceof HTMLInputElement) el.value = ch;
  else el.textContent = ch;
}

function buildKeyboardCell(i: number): HTMLInputElement {
  const cell = document.createElement("input");
  cell.type = "text";
  cell.maxLength = 1;
  cell.className = "prerun-namecell";
  cell.autocomplete = "off";
  cell.spellcheck = false;
  cell.inputMode = "text";
  cell.setAttribute("aria-label", `initial ${i + 1}`);
  return cell;
}

function buildCycleCell(i: number): HTMLDivElement {
  const cell = document.createElement("div");
  // Same visual class as the input cells; --cycle adds the tappable affordance.
  // Deliberately NO tabindex: the cell must never become DOM-focusable (focus
  // is the iOS crash trigger). role=button is AX-only and does not add focus.
  cell.className = "prerun-namecell prerun-namecell--cycle";
  cell.setAttribute("role", "button");
  cell.setAttribute("aria-label", `initial ${i + 1} — tap to change`);
  return cell;
}

/**
 * Build the 3-cell callsign field.
 *
 * @param mode  Overridable for tests; defaults to the platform mode.
 */
export function buildNameInput(
  parent: HTMLElement,
  stepHead: HTMLElement,
  metaState: MetaState,
  hooks: NameInputHooks,
  mode: InitialsInputMode = defaultInputMode,
): void {
  const callsign = document.createElement("div");
  callsign.className = "prerun-callsign";
  callsign.appendChild(stepHead);

  const field = document.createElement("div");
  field.className = "prerun-namefield";

  const cells: HTMLElement[] = [];
  for (let i = 0; i < INITIALS_LENGTH; i++) {
    const cell = mode === "cycle" ? buildCycleCell(i) : buildKeyboardCell(i);
    cells.push(cell);
    field.appendChild(cell);
  }

  // ── Pre-fill ──────────────────────────────────────────────────────────────
  if (mode === "cycle") {
    // Native: always seed with the current (sanitized) initials so the field is
    // never empty/invalid — the deploy gate is satisfied out of the box and the
    // player only taps if they want to change it. Default is "AAA".
    const chars = sanitizeInitials(metaState.playerInitials).split("");
    for (let i = 0; i < INITIALS_LENGTH; i++) {
      writeCellChar(cells[i]!, chars[i] ?? INITIALS_FALLBACK);
    }
  } else {
    // Web: only seed when the player has set non-default initials before, so a
    // first-time player sees empty cells (and is prompted to type).
    const known = metaState.playerInitials !== DEFAULT_INITIALS;
    if (known) {
      const chars = metaState.playerInitials.split("");
      for (let i = 0; i < Math.min(chars.length, INITIALS_LENGTH); i++) {
        writeCellChar(cells[i]!, chars[i]!);
      }
    }
  }

  const getValue = (): string => cells.map(readCellChar).join("");

  const commit = (): void => {
    const raw = getValue();
    metaState.playerInitials = sanitizeInitials(raw);
    hooks.onInput(raw);
  };

  const updateValidity = (): void => {
    field.classList.toggle("invalid", getValue().trim().length < 1);
  };

  if (mode === "cycle") {
    wireCycleCells(cells as HTMLDivElement[], commit, updateValidity);
  } else {
    wireKeyboardCells(cells as HTMLInputElement[], commit, updateValidity);
  }

  callsign.appendChild(field);
  parent.appendChild(callsign);

  updateValidity();

  // Auto-focus the first cell — KEYBOARD MODE ONLY. In cycle mode the cells are
  // <div>s that never take focus (the whole point), so there is nothing to focus
  // and the keyboard is never summoned.
  if (mode === "keyboard") {
    const known = metaState.playerInitials !== DEFAULT_INITIALS;
    requestAnimationFrame(() => {
      const first = cells[0];
      if (first instanceof HTMLInputElement) {
        first.focus();
        if (known) first.select();
      }
    });
  }
}

/** Native: a tap on a cell cycles its letter (top half ↑ back, bottom ↓ next). */
function wireCycleCells(
  cells: HTMLDivElement[],
  commit: () => void,
  updateValidity: () => void,
): void {
  for (const cell of cells) {
    cell.addEventListener("click", (e: MouseEvent) => {
      // clientY and the bounding rect are both viewport-space, so the stage's
      // `transform: scale()` cancels out. Default to forward if geometry is 0
      // (e.g. a synthetic click in a headless/hidden layout).
      const rect = cell.getBoundingClientRect();
      const dir: 1 | -1 =
        rect.height > 0 && e.clientY < rect.top + rect.height / 2 ? -1 : 1;
      const next = cycleInitialChar(readCellChar(cell) || INITIALS_FALLBACK, dir);
      writeCellChar(cell, next);
      commit();
      updateValidity();
    });
  }
}

/** Web/desktop: real OS-keyboard text entry with auto-advance + backspace. */
function wireKeyboardCells(
  cells: HTMLInputElement[],
  commit: () => void,
  updateValidity: () => void,
): void {
  for (let i = 0; i < cells.length; i++) {
    const cell = cells[i]!;

    cell.addEventListener("input", () => {
      // Filter to charset, one char max.
      const filtered = cell.value
        .toUpperCase()
        .split("")
        .filter((ch) => INITIALS_CHARSET.includes(ch))
        .join("")
        .slice(0, 1);
      cell.value = filtered;
      if (filtered && i < cells.length - 1) {
        cells[i + 1]!.focus();
      }
      commit();
      updateValidity();
    });

    cell.addEventListener("keydown", (e: KeyboardEvent) => {
      if (e.key === "Escape") return; // let Escape bubble to BACK handler
      e.stopPropagation();
      if (e.key === "Backspace" && !cell.value && i > 0) {
        cells[i - 1]!.focus();
      }
      if (e.key === "Enter") {
        e.preventDefault();
        cell.blur();
      }
    });
  }
}
